import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { authAPI } from '../utils/api';

function AdminSettings({ user, onUserUpdate }) {
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Username change state
  const [usernameData, setUsernameData] = useState({
    newUsername: '',
    password: '',
  });
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);

    try {
      await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      toast.success('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    setUsernameError('');

    if (usernameData.newUsername.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      return;
    }

    setUsernameLoading(true);

    try {
      const response = await authAPI.changeUsername({
        newUsername: usernameData.newUsername,
        password: usernameData.password,
      });

      // Update token and user in localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Notify parent component
      if (onUserUpdate) {
        onUserUpdate(response.data.user);
      }

      toast.success('Username changed successfully');
      setUsernameData({
        newUsername: '',
        password: '',
      });
    } catch (err) {
      setUsernameError(err.response?.data?.error || 'Failed to change username');
    } finally {
      setUsernameLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <div className="admin-page">
      <h2>Account Settings</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Manage your admin account credentials
      </p>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 500 }}>
        {/* Current User Info */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Account
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <PersonIcon fontSize="small" />
              <Typography>
                Logged in as: <strong>{user?.username || 'admin'}</strong>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Change Username */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PersonIcon color="primary" />
              <Typography variant="h6">Change Username</Typography>
            </Box>

            {usernameError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {usernameError}
              </Alert>
            )}

            <form onSubmit={handleUsernameChange}>
              <TextField
                fullWidth
                label="New Username"
                value={usernameData.newUsername}
                onChange={(e) =>
                  setUsernameData({ ...usernameData, newUsername: e.target.value })
                }
                required
                sx={{ mb: 2 }}
                inputProps={{ minLength: 3 }}
              />

              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={usernameData.password}
                onChange={(e) =>
                  setUsernameData({ ...usernameData, password: e.target.value })
                }
                required
                sx={{ mb: 2 }}
                helperText="Enter your password to confirm"
              />

              <Button
                type="submit"
                variant="contained"
                disabled={usernameLoading}
                fullWidth
              >
                {usernameLoading ? 'Updating...' : 'Change Username'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LockIcon color="primary" />
              <Typography variant="h6">Change Password</Typography>
            </Box>

            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}

            <form onSubmit={handlePasswordChange}>
              <TextField
                fullWidth
                label="Current Password"
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, currentPassword: e.target.value })
                }
                required
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('current')}
                        edge="end"
                      >
                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, newPassword: e.target.value })
                }
                required
                sx={{ mb: 2 }}
                inputProps={{ minLength: 8 }}
                helperText="Minimum 8 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('new')}
                        edge="end"
                      >
                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                }
                required
                sx={{ mb: 2 }}
                error={
                  passwordData.confirmPassword !== '' &&
                  passwordData.newPassword !== passwordData.confirmPassword
                }
                helperText={
                  passwordData.confirmPassword !== '' &&
                  passwordData.newPassword !== passwordData.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('confirm')}
                        edge="end"
                      >
                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                disabled={passwordLoading}
                fullWidth
              >
                {passwordLoading ? 'Updating...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Security Tips:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>Use a strong, unique password</li>
              <li>Don't share your credentials with others</li>
              <li>Change your password regularly</li>
              <li>You will be automatically logged out after 10 minutes of inactivity</li>
            </ul>
          </Typography>
        </Alert>
      </Box>
    </div>
  );
}

export default AdminSettings;
